// Lightweight client-side funnel tracking.
// One persistent anon session_id in localStorage; every track() call POSTs a row.
// UTM params are captured on landing and auto-attached to every event for 30 days
// so we can attribute the full Magic-flow funnel back to the traffic source.
import { api } from "@/lib/api";

const SESSION_KEY = "af_session_id";
const UTM_KEY = "af_utm";
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

export function sessionId() {
  if (typeof window === "undefined") return "";
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    window.localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/**
 * Read UTM params from the current URL and persist them to localStorage.
 * Idempotent — only overwrites if the new URL actually has UTMs (so a deep-link
 * without UTMs doesn't wipe the original attribution).
 *
 * Should be called once on the landing page mount.
 */
export function captureUtm() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const found = {};
    for (const k of UTM_KEYS) {
      const v = url.searchParams.get(k);
      if (v) found[k] = String(v).slice(0, 80);
    }
    if (Object.keys(found).length === 0) return;
    found._captured_at = Date.now();
    window.localStorage.setItem(UTM_KEY, JSON.stringify(found));
  } catch {
    // never break the page
  }
}

/**
 * Return the persisted UTM dict (without internal metadata), or null if
 * nothing was captured or the capture has expired.
 */
export function getUtm() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(UTM_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || Date.now() - (obj._captured_at || 0) > UTM_TTL_MS) {
      window.localStorage.removeItem(UTM_KEY);
      return null;
    }
    const out = {};
    for (const k of UTM_KEYS) if (obj[k]) out[k] = obj[k];
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

export async function track(event_type, payload = {}) {
  // Pull tier/cycle to top-level; everything else flows into metadata so the
  // backend `metadata.*` aggregations (e.g. magic-funnel by source/utm_source)
  // actually see the data.
  const { tier, cycle, ...rest } = payload || {};
  const meta = { ...rest };

  const utm = getUtm();
  if (utm) Object.assign(meta, utm);

  try {
    await api.post("/analytics/event", {
      event_type,
      session_id: sessionId(),
      tier: tier || null,
      cycle: cycle || null,
      metadata: Object.keys(meta).length ? meta : null,
    });
  } catch {
    // Analytics should never break the user's flow.
  }
}
